import type { Agent } from '@studio/models/agent';
import { Protocol as VideoProtocol } from '@studio/models/video';
import { useCallback, useEffect, useRef } from 'react';
import { identifyHost } from '#~/device/discovery/commands/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import {
  addCentralDispatchSubscriber,
  clearCentralDispatchSubscriber
} from '#~/statequery/central-dispatch.js';
import {
  multiProxyDeviceDiscoveryStateQuery,
  multiProxyStateQuery,
  transformMultiProxyDeviceDiscoveryToDiscovery
} from '#~/statequery/queries.js';
import { discoveryHostKey } from '../source/discovery-mismatch.js';
import type { Discovery as SourceDiscovery } from '../source/types.js';

/**
 * ENG-4927 — identify at address.
 *
 * When the operator types an IP in Add Source → RTSP and presses Check
 * Connectivity without having identified the camera, Studio asks the Bridge
 * to probe that host (`identifyHost`) and waits up to `IdentifyWindowMs` for
 * that same Bridge to publish a model-identified discovery row there. The wait
 * is bounded and never guesses: an unmatched camera resolves `undefined`.
 */

/** How long Studio waits for the Bridge's identify answer (ticket: "within 3 s"). */
export const IdentifyWindowMs = 3000;

/**
 * A discovery row the Bridge identified down to a registry model. `brandId` is
 * optional: the fingerprint matcher returns only the model id for a
 * model-level match, and the RTSP step resolves the brand from the model's
 * template (rtsp.tsx's template brand seed).
 */
export type IdentifiedDiscovery = Discovery &
  Readonly<{
    modelId: NonNullable<Discovery['modelId']>;
  }>;

/** Hosts compare case-insensitively (hostnames) and ignore surrounding whitespace. */
const normalizeHost = (host: string): string => host.trim().toLowerCase();

/**
 * The host a discovery row sits at, for matching against the typed address.
 *
 * `discoveryHostKey` covers NDI (`.video.host`) and ONVIF (`.control.host`),
 * the mechanisms cross-protocol reconciliation needs. The identify answer is
 * neither: the Bridge publishes it through `DeriveRtspRowAtHost`, which
 * `pbToJsDiscoveryVideo` maps to an RTSP `video` aspect with the probed host.
 * So RTSP rows are read here as well, and only here — the reconciliation
 * helper keeps its narrower contract. Per rule: stay-in-scope (ai.knowledge/global).
 */
const identifiedRowHost = (row: Discovery): string | undefined => {
  // The selector's aggregated `Discovery` is the general one narrowed to a
  // control/video aspect; `discoveryHostKey` re-checks the aspect at runtime
  // (`'video' in` / `'control' in`), so the widening is safe here as it is in
  // source/selector.tsx.
  const key = discoveryHostKey(row as SourceDiscovery);

  if (key !== undefined) {
    return key;
  }

  if ('video' in row && row.video?.protocol === VideoProtocol.RTSP) {
    const host = (row.video as { host?: string }).host;

    return host && host.trim() !== '' ? host : undefined;
  }

  return undefined;
};

/**
 * First discovery row at `host` that carries a `modelId`.
 *
 * A model-level match carries no `brandId` (the matcher resolves the model
 * alone), so the model is what counts. A brand-only match (brandId without
 * modelId) is not an identification the RTSP step can act on — the model
 * picks the template — so it is ignored.
 */
export const findIdentifiedRowAtHost = (
  rows: readonly Discovery[],
  host: string
): IdentifiedDiscovery | undefined => {
  const wanted = normalizeHost(host);

  if (wanted === '') {
    return undefined;
  }

  return rows.find((row): row is IdentifiedDiscovery => {
    if (!row.modelId) {
      return false;
    }

    const rowHost = identifiedRowHost(row);

    return rowHost !== undefined && normalizeHost(rowHost) === wanted;
  });
};

/** Dependencies of the identify-at-host wait, injected so the wait is testable without React or the broker. */
export type IdentifyAtHostDeps = Readonly<{
  /** Sends the identify command to the Bridge. Its outcome never ends the wait — the window does. */
  identifyHost: (agentId: Agent['id'], host: string) => Promise<unknown>;
  /**
   * Current discovery rows of the Bridge `agentId` only. Private IPs repeat
   * across sites, so a row another Bridge published at the same address is a
   * different camera and must never answer this probe.
   */
  readRows: (agentId: Agent['id']) => readonly Discovery[];
  /** Calls `onChange` whenever Bridge state may have changed; returns the unsubscribe. */
  subscribe: (onChange: () => void) => () => void;
  /** Wait window in milliseconds. */
  windowMs: number;
}>;

/** A pending identify-at-host wait. `cancel` resolves the promise with `undefined`. */
export type IdentifyAtHostHandle = Readonly<{
  promise: Promise<IdentifiedDiscovery | undefined>;
  cancel: () => void;
}>;

/**
 * Identify the camera at `host` through `agentId`.
 *
 * Resolves with the identified row as soon as one is published, or with
 * `undefined` once `windowMs` elapses or the wait is cancelled. A row already
 * present (an earlier scan matched this host) resolves immediately without a
 * probe.
 *
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — hit, timeout
 * and cancel all go through `settle`, which clears the timer and the
 * subscription exactly once.
 */
export const identifyAtHost = (
  deps: IdentifyAtHostDeps,
  agentId: Agent['id'],
  host: string
): IdentifyAtHostHandle => {
  let settled = false;
  let unsubscribe: () => void = () => undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let resolveWith: (row: IdentifiedDiscovery | undefined) => void = () => undefined;

  const promise = new Promise<IdentifiedDiscovery | undefined>((resolve) => {
    resolveWith = resolve;
  });

  const settle = (row: IdentifiedDiscovery | undefined): void => {
    if (settled) {
      return;
    }

    settled = true;

    if (timer !== undefined) {
      clearTimeout(timer);
    }

    unsubscribe();
    resolveWith(row);
  };

  const check = (): void => {
    const row = findIdentifiedRowAtHost(deps.readRows(agentId), host);

    if (row) {
      settle(row);
    }
  };

  // Already identified by an earlier scan: no probe needed.
  check();

  if (!settled) {
    timer = setTimeout(() => settle(undefined), deps.windowMs);
    unsubscribe = deps.subscribe(check);
    // A declined/failed command does not end the wait: a concurrent discovery
    // cycle may still publish the row, and the window bounds the wait anyway.
    void deps.identifyHost(agentId, host).then(undefined, () => undefined);
  }

  return { promise, cancel: () => settle(undefined) };
};

/**
 * Discovery rows of one Bridge, shaped as the selector table reads them. The
 * proxy map is keyed by agent id (`multiProxyStateQuery`), so only the probed
 * Bridge's map goes through the transform.
 */
const readLiveRows = (agentId: Agent['id']): Discovery[] => {
  const byProxy = multiProxyStateQuery(multiProxyDeviceDiscoveryStateQuery);
  // `multiProxyStateQuery`'s generic drops the per-proxy level its runtime
  // value has (one map per proxy); the transform takes the real shape. Same
  // suppression as source/selector.tsx, for the same reason.
  // @ts-expect-error
  const transformed = transformMultiProxyDeviceDiscoveryToDiscovery({
    [agentId]: byProxy[agentId] ?? {}
  });

  return Object.entries(transformed).map(([id, device]) => ({
    ...device.value,
    id: id as Discovery['id']
  }));
};

/**
 * Re-check on every Bridge state tick. `minInterval: 0` on purpose: a throttled
 * general subscriber DROPS the ticks it skips (central-dispatch.ts), and the
 * identified row may land on the last tick inside the window.
 */
const subscribeLive = (onChange: () => void): (() => void) => {
  const id = addCentralDispatchSubscriber(onChange, { minInterval: 0 });

  return () => {
    clearCentralDispatchSubscriber(id);
  };
};

const liveDeps: IdentifyAtHostDeps = {
  identifyHost,
  readRows: readLiveRows,
  subscribe: subscribeLive,
  windowMs: IdentifyWindowMs
};

/**
 * Identify-at-host bound to the live Bridge state, for callers that run
 * several waits at once (the multi-add save, ENG-4940). Each handle is
 * independent; the caller owns cancelling them.
 */
export const identifyAtHostLive = (agentId: Agent['id'], host: string): IdentifyAtHostHandle =>
  identifyAtHost(liveDeps, agentId, host);

/** The function `useIdentifyAtHost` returns. */
export type IdentifyAtHost = (
  agentId: Agent['id'],
  host: string
) => Promise<IdentifiedDiscovery | undefined>;

/**
 * Identify-at-host bound to the live Bridge state.
 *
 * One wait at a time: a new call cancels the previous one (the latest Check
 * Connectivity is the one the operator means), and unmount cancels the
 * pending wait so a late answer never reaches an unmounted step.
 */
export const useIdentifyAtHost = (): IdentifyAtHost => {
  const activeRef = useRef<IdentifyAtHostHandle | undefined>(undefined);

  useEffect(
    () => () => {
      activeRef.current?.cancel();
    },
    []
  );

  return useCallback((agentId, host) => {
    activeRef.current?.cancel();

    const handle = identifyAtHost(liveDeps, agentId, host);
    activeRef.current = handle;

    return handle.promise;
  }, []);
};
